(() => {
  const ACTIONS = ['REFUND', 'EXCHANGE', 'STORE_CREDIT'];
  const REASONS = ['TOO_SMALL', 'TOO_LARGE', 'TOO_NARROW', 'TOO_WIDE', 'CHANGED_MIND', 'QUALITY_ISSUE', 'WRONG_ITEM', 'OTHER'];
  const STATUSES = ['REQUESTED', 'APPROVED', 'REJECTED', 'WAITING_FOR_PARCEL', 'RECEIVED', 'PROCESSING', 'REFUNDED', 'EXCHANGED', 'STORE_CREDIT_ISSUED', 'UNDER_REVIEW', 'CANCELLED'];
  const FLOW_STEPS = ['lookup', 'items', 'details', 'review'];
  const FLOW_STORAGE_KEY = 'mymara:returns:flow';
  const escape = (value = '') => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const checked = (value) => value ? ' checked' : '';

  class MyMaraReturns extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('pageshow', this.onPageShow);
        if (this.state.step === 'status') this.loadStatus();
        else {
          if (!this.hasMemoryPhotos()) this.restoreFlowState();
          this.render(false);
          this.loadExchangeOptionsForCurrentStep();
        }
        return;
      }
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-returns-strings]').textContent);
      this.api = new window.MyMaraReturnsApi();
      this.photoMaxCount = Math.max(1, Math.min(20, Number(this.dataset.photoMaxCount) || 5));
      this.photoMaxBytes = Math.max(1, Math.min(25, Number(this.dataset.photoMaxSizeMb) || 10)) * 1024 * 1024;
      this.view = this.querySelector('[data-returns-view]');
      this.errorBox = this.querySelector('[data-returns-error]');
      this.announcement = this.querySelector('[data-returns-announcement]');
      this.progress = this.querySelector('[data-returns-progress]');
      const initialFields = this.querySelector('[data-returns-initial-fields]');
      if (initialFields) initialFields.disabled = false;
      this.prefillOrderNumber();
      this.lookupMarkup = this.view.innerHTML;
      this.spinner = this.querySelector('[data-returns-spinner]').innerHTML;
      this.state = { step: 'lookup', sessionToken: null, expiresAt: 0, order: null, selectedItems: new Map(), busy: false, attempt: null, acknowledged: false };
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('error', (event) => {
        if (event.target.matches('[data-returns-image]')) {
          event.target.hidden = true;
          event.target.nextElementSibling.hidden = false;
        }
      }, true);
      this.onPageHide = (event) => {
        this.wasStatusPage = this.state.step === 'status';
        if (!this.wasStatusPage) this.persistFlowState(this.state.busy && this.state.step === 'review');
        if (event.persisted && !this.wasStatusPage && this.state.step === 'lookup') {
          const email = this.querySelector('[name="email"]');
          if (email) email.value = '';
        }
      };
      this.onPageShow = (event) => {
        if (event.persisted && this.wasStatusPage) {
          this.state.step = 'status';
          this.loadStatus();
        } else if (event.persisted) {
          this.state.busy = false;
          if (!this.hasMemoryPhotos()) this.restoreFlowState();
          this.render(false);
          this.loadExchangeOptionsForCurrentStep();
        }
      };
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      const url = new URL(window.location.href);
      if (this.dataset.mode === 'status' || /\/returns\/status\/?$/.test(url.pathname) || window.MymaraReturnsStatusToken !== undefined || url.searchParams.has('token')) {
        this.state.step = 'status';
        this.loadStatus();
      } else {
        this.restoreFlowState();
        this.syncStepInUrl(this.state.step);
        this.progress.hidden = false;
        this.render(false);
        this.loadExchangeOptionsForCurrentStep();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.expiryTimer);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.state.step !== 'status') this.persistFlowState(this.state.busy && this.state.step === 'review');
      this.clearSession(false);
      this.state.busy = false;
      if (this.state.step !== 'status') this.state.step = 'lookup';
    }

    t(key, values = {}) {
      let text = this.strings[key] || '';
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`[${name}]`, String(value)); });
      return text;
    }

    button(action, key, primary = false, disabled = false) {
      return `<button type="button" class="${primary ? 'btn btn-primary' : 'btn btn-outline'}" data-returns-action="${action}"${disabled ? ' disabled' : ''}>${escape(this.t(key))}</button>`;
    }

    heading(key) {
      return `<h1 class="h2" tabindex="-1" data-returns-heading>${escape(this.t(key))}</h1>`;
    }

    updateProgress() {
      const steps = ['lookup', 'items', 'details', 'review'];
      this.progress.hidden = !steps.includes(this.state.step);
      this.progress.querySelectorAll('li').forEach((item, index) => {
        if (steps[index] === this.state.step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    render(focus = true) {
      this.hideError();
      this.updateProgress();
      const renderers = { lookup: () => this.lookupMarkup, items: () => this.renderItems(), details: () => this.renderDetails(), review: () => this.renderReview(), confirmation: () => this.renderConfirmation() };
      this.view.innerHTML = renderers[this.state.step]();
      this.updatePhotoNavigation();
      if (this.photoSubmissionInterrupted) this.view.querySelectorAll('button, input, textarea, select').forEach((control) => { control.disabled = true; });
      if (this.photoSubmissionInterrupted) this.showError('error_photo_interrupted');
      if (focus) this.view.querySelector('[data-returns-heading]')?.focus();
    }

    // Deep link from chat support: /pages/tagasta-toode/?order=1234 fills in the order number only.
    // The email is never taken from the URL; the customer types it, which is what proves it is their order.
    prefillOrderNumber() {
      const field = this.querySelector('[name="orderNumber"]');
      if (!field || !window.location?.href) return;
      const raw = new URL(window.location.href).searchParams.get('order');
      const match = typeof raw === 'string' ? /^#?\s?([A-Za-z0-9_-]{1,40})$/.exec(raw.trim()) : null;
      if (!match) return;
      field.value = `#${match[1]}`;
      field.setAttribute('value', field.value);
    }

    go(step) {
      if (this.state.busy || this.state.attempt?.uncertain || this.photoSubmissionInterrupted) return;
      this.state.step = step;
      this.syncStepInUrl(step);
      this.persistFlowState();
      this.render();
      this.loadExchangeOptionsForCurrentStep();
    }

    syncStepInUrl(step) {
      if (!window.location?.href || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      if (FLOW_STEPS.includes(step)) url.searchParams.set('step', step);
      else url.searchParams.delete('step');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    flowOrderSnapshot() {
      const order = this.state.order;
      if (!order) return null;
      return {
        orderNumber: order.orderNumber,
        items: (order.items || []).map((item) => ({
          lineItemReference: item.lineItemReference,
          productTitle: item.productTitle,
          variantTitle: item.variantTitle,
          size: item.size,
          imageUrl: item.imageUrl,
          quantityPurchased: item.quantityPurchased,
          quantityReturnable: item.quantityReturnable,
          unitPrice: item.unitPrice,
          currencyCode: item.currencyCode,
          eligibility: item.eligibility?.eligible === true
            ? { eligible: true }
            : { eligible: false, reasonCode: item.eligibility?.reasonCode }
        }))
      };
    }

    persistFlowState(pendingSubmission = false) {
      if (!this.state?.sessionToken || !this.state.order || this.state.expiresAt <= Date.now() || !FLOW_STEPS.includes(this.state.step)) return;
      if (this.hasMemoryPhotos()) {
        try {
          window.sessionStorage.removeItem(FLOW_STORAGE_KEY);
          if (this.state.attempt && (this.state.attempt.uncertain || pendingSubmission)) {
            window.sessionStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify({ photoSubmissionInterrupted: true, expiresAt: this.state.expiresAt }));
          }
        } catch {}
        return;
      }
      const selectedItems = [...this.state.selectedItems].map(([reference, detail]) => [reference, {
        quantity: detail.quantity,
        requestedAction: detail.requestedAction,
        reason: detail.reason,
        customerComment: detail.customerComment,
        requestedExchangeVariantReference: detail.requestedExchangeVariantReference
      }]);
      const attempt = this.state.attempt ? {
        key: this.state.attempt.key,
        body: this.state.attempt.body,
        uncertain: this.state.attempt.uncertain || pendingSubmission
      } : null;
      const snapshot = {
        step: this.state.step,
        sessionToken: this.state.sessionToken,
        expiresAt: this.state.expiresAt,
        order: this.flowOrderSnapshot(),
        selectedItems,
        acknowledged: this.state.acknowledged,
        attempt
      };
      try { window.sessionStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(snapshot)); } catch {}
    }

    restoreFlowState() {
      let snapshot;
      try { snapshot = JSON.parse(window.sessionStorage.getItem(FLOW_STORAGE_KEY)); } catch {
        try { window.sessionStorage.removeItem(FLOW_STORAGE_KEY); } catch {}
        return false;
      }
      if (snapshot?.photoSubmissionInterrupted) {
        this.photoSubmissionInterrupted = true;
        return false;
      }
      if (!snapshot || !snapshot.sessionToken || !Number.isFinite(snapshot.expiresAt) || snapshot.expiresAt <= Date.now() || !Array.isArray(snapshot.order?.items)) {
        try { window.sessionStorage.removeItem(FLOW_STORAGE_KEY); } catch {}
        return false;
      }

      const orderItems = snapshot.order.items.filter((item) => item?.lineItemReference && typeof item.productTitle === 'string');
      if (!orderItems.length) return false;
      const order = { orderNumber: String(snapshot.order.orderNumber || ''), items: orderItems };
      const validReferences = new Set(orderItems.filter((item) => item.eligibility?.eligible === true).map((item) => item.lineItemReference));
      const savedSelections = Array.isArray(snapshot.selectedItems) ? snapshot.selectedItems : [];
      const selectedItems = new Map(savedSelections.filter((entry) => Array.isArray(entry) && validReferences.has(entry[0])).map(([reference, detail]) => [reference, {
        quantity: Number(detail?.quantity) || 1,
        requestedAction: ACTIONS.includes(detail?.requestedAction) ? detail.requestedAction : '',
        reason: REASONS.includes(detail?.reason) ? detail.reason : '',
        customerComment: typeof detail?.customerComment === 'string' ? detail.customerComment.slice(0, 2000) : '',
        requestedExchangeVariantReference: typeof detail?.requestedExchangeVariantReference === 'string' ? detail.requestedExchangeVariantReference : '',
        photos: [],
        options: null,
        optionsLoading: false,
        optionsError: false
      }]));
      const url = new URL(window.location.href);
      const requestedStep = url.searchParams.get('step');
      const storedStep = FLOW_STEPS.includes(snapshot.step) ? snapshot.step : 'items';
      let step = FLOW_STEPS.includes(requestedStep) ? requestedStep : storedStep;
      if (['details', 'review'].includes(step) && selectedItems.size === 0) step = 'items';
      const attempt = snapshot.attempt?.key && snapshot.attempt?.body ? {
        key: snapshot.attempt.key,
        body: snapshot.attempt.body,
        uncertain: snapshot.attempt.uncertain === true
      } : null;
      this.state = {
        ...this.state,
        step,
        sessionToken: snapshot.sessionToken,
        expiresAt: snapshot.expiresAt,
        order,
        selectedItems,
        acknowledged: snapshot.acknowledged === true,
        attempt,
        busy: false
      };
      this.scheduleExpiry();
      this.syncStepInUrl(step);
      return true;
    }

    loadExchangeOptionsForCurrentStep() {
      if (this.state.step !== 'details') return;
      this.state.selectedItems.forEach((detail, reference) => {
        if (detail.requestedAction === 'EXCHANGE' && !detail.options) this.loadExchange(reference);
      });
    }

    hideError() {
      this.errorBox.hidden = true;
      this.errorBox.replaceChildren();
      this.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      });
    }

    showError(key, action, field) {
      this.errorBox.innerHTML = `<p>${escape(this.t(key))}</p>${action ? this.button(action, { refresh: 'refresh_order', restart: 'find_again', retry_status: 'retry' }[action]) : ''}`;
      this.errorBox.hidden = false;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', this.errorBox.id);
        field.focus();
      } else this.errorBox.focus();
    }

    handleError(error, context) {
      if (error.code === 'SESSION_EXPIRED') {
        this.clearSession();
        this.state.step = 'lookup';
        this.syncStepInUrl('lookup');
        this.render();
        this.showError('error_session', 'restart');
        return;
      }
      if (error.code === 'RETURN_CONFLICT' || error.code === 'RETURN_NOT_ELIGIBLE') {
        this.showError('error_conflict', 'refresh');
        return;
      }
      if (context === 'lookup' && ['INVALID_ORDER_DETAILS', 'VALIDATION_ERROR'].includes(error.code)) {
        this.showError('error_lookup');
        return;
      }
      if (error.status === 429 || error.code === 'RATE_LIMITED') {
        this.showError('error_rate');
        return;
      }
      if (context === 'create' && ['EXCHANGE_OUT_OF_STOCK', 'EXCHANGE_VARIANT_INVALID'].includes(error.code)) {
        this.state.attempt = null;
        this.state.selectedItems.forEach((detail) => {
          if (detail.requestedAction === 'EXCHANGE') {
            detail.options = null;
            detail.requestedExchangeVariantReference = '';
          }
        });
        this.go('details');
        this.showError('error_exchange');
        return;
      }
      if (context === 'create' && error.code?.startsWith('PHOTO_')) {
        this.state.attempt = null;
        const match = /^items\.(\d+)\./.exec(error.field || '');
        const affected = match ? [[...this.state.selectedItems.values()][Number(match[1])]] : [...this.state.selectedItems.values()].filter((detail) => detail.reason === 'QUALITY_ISSUE');
        if (['PHOTO_UPLOAD_EXPIRED', 'PHOTO_NOT_FOUND', 'PHOTO_NOT_CONFIRMED', 'PHOTO_ALREADY_USED'].includes(error.code)) {
          affected.forEach((detail) => detail?.photos?.forEach((photo) => { photo.status = 'failed'; photo.error = error.code; }));
        }
        this.go('details');
        this.showError(this.photoErrorKey(error));
        return;
      }
      const formErrors = ['VALIDATION_ERROR', 'INVALID_QUANTITY', 'INVALID_ACTION', 'COMMENT_REQUIRED', 'EXCHANGE_VARIANT_REQUIRED', 'IDEMPOTENCY_CONFLICT'];
      if (formErrors.includes(error.code)) {
        if (context === 'create') {
          this.state.attempt = null;
          this.go('details');
        }
        const match = /^items\.(\d+)\.(\w+)$/.exec(error.field || '');
        const reference = match ? [...this.state.selectedItems.keys()][Number(match[1])] : null;
        const group = reference && this.detailElement(reference);
        this.showError('error_validation', null, group?.querySelector(`[data-detail-field="${match[2]}"]`));
        return;
      }
      this.showError(context === 'create' && this.state.attempt?.uncertain ? 'error_uncertain' : 'error_unavailable', context === 'status' ? 'retry_status' : null);
    }

    setBusy(busy, key = 'loading') {
      this.state.busy = busy;
      this.persistFlowState(busy && this.state.step === 'review');
      this.view.setAttribute('aria-busy', String(busy));
      this.announcement.textContent = busy ? this.t(key) : '';
      this.view.querySelectorAll('button, input, textarea, select').forEach((control) => {
        if (busy) {
          control.dataset.returnsWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if (control.dataset.returnsWasDisabled !== undefined) {
          control.disabled = control.dataset.returnsWasDisabled === 'true';
          delete control.dataset.returnsWasDisabled;
        }
      });
      const submit = this.view.querySelector('[type="submit"]');
      if (submit) {
        if (busy) {
          this.submitMarkup = submit.innerHTML;
          submit.innerHTML = `${this.spinner}<span>${escape(this.t(key))}</span>`;
        } else if (this.submitMarkup) {
          submit.innerHTML = this.submitMarkup;
          this.submitMarkup = null;
        }
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.state.busy || this.photoSubmissionInterrupted) return;
      if (this.state.step === 'lookup') {
        const form = event.target;
        if (!form.reportValidity()) return;
        this.hideError();
        this.setBusy(true, 'finding_order');
        try {
          const data = await this.api.lookupOrder(form.elements.orderNumber.value.trim(), form.elements.email.value.trim());
          if (!data.sessionToken || !Array.isArray(data.order?.items) || !Number.isFinite(Date.parse(data.expiresAt))) throw new window.MyMaraReturnsApiError('SERVER_ERROR', 502);
          this.clearSession();
          this.state.sessionToken = data.sessionToken;
          this.state.expiresAt = Date.parse(data.expiresAt);
          this.state.order = data.order;
          form.elements.email.value = '';
          this.setBusy(false);
          this.go('items');
          this.scheduleExpiry();
        } catch (error) {
          this.setBusy(false);
          this.handleError(error, 'lookup');
        }
      } else if (this.state.step === 'details') {
        if (this.validateDetails(event.target)) this.go('review');
      } else if (this.state.step === 'review') {
        if (!event.target.reportValidity()) return;
        await this.submitReturn();
      }
    }

    scheduleExpiry() {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = setTimeout(() => {
        if (this.state.busy || this.state.attempt?.uncertain) {
          this.expiryTimer = setTimeout(() => this.scheduleExpiry(), 1000);
        } else this.handleError({ code: 'SESSION_EXPIRED' });
      }, Math.max(0, this.state.expiresAt - Date.now()));
    }

    clearSession(clearStored = true, removePhotos = true) {
      clearTimeout(this.expiryTimer);
      if (clearStored) {
        try { window.sessionStorage.removeItem(FLOW_STORAGE_KEY); } catch {}
      }
      if (this.state.step === 'confirmation') {
        this.confirmation = null;
        this.confirmationItems = null;
      }
      this.state.selectedItems.forEach((detail) => this.disposePhotos(detail, this.state.sessionToken, removePhotos));
      this.state.sessionToken = null;
      this.state.expiresAt = 0;
      this.state.order = null;
      this.state.selectedItems = new Map();
      this.state.attempt = null;
      this.state.acknowledged = false;
    }

    item(reference) {
      return this.state.order?.items.find((item) => item.lineItemReference === reference);
    }

    summary() {
      const productCount = this.state.order.items.length;
      return `<div class="mymara-returns__order"><strong>${escape(this.state.order.orderNumber)}</strong>${this.state.order.customerName ? `<span>${escape(this.state.order.customerName)}</span>` : ''}<span>${escape(this.t(productCount === 1 ? 'order_items_one' : 'order_items', { count: productCount }))}</span></div>`;
    }

    image(item) {
      let url = '';
      try {
        const parsed = new URL(item.imageUrl);
        if (parsed.protocol === 'https:') url = parsed.href;
      } catch {}
      return `<div class="mymara-returns__image">${url ? `<img data-returns-image src="${escape(url)}" alt="" width="160" height="160" loading="lazy" referrerpolicy="no-referrer">` : ''}<span${url ? ' hidden' : ''}>${escape(this.t('no_image'))}</span></div>`;
    }

    productText(item, includeSize = true) {
      let variantTitle = String(item.variantTitle || '').trim();
      if (variantTitle && item.size) {
        const size = String(item.size).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        variantTitle = variantTitle.replace(new RegExp(`(?:^|[\\s/|·,–-])(?:size|suurus|talla|tamaño)?\\s*${size}(?=$|[\\s/|·,–-])`, 'ig'), ' ').replace(/^[\\s/|·,–-]+|[\\s/|·,–-]+$/g, '').trim();
      }
      return `<strong>${escape(item.productTitle)}</strong>${variantTitle ? `<span class="mymara-returns__variant">${escape(variantTitle)}</span>` : ''}${includeSize && item.size ? `<span>${escape(this.t('size', { size: item.size }))}</span>` : ''}`;
    }

    money(item) {
      if (!item.unitPrice || !item.currencyCode) return '';
      try { return new Intl.NumberFormat(this.lang || document.documentElement.lang, { style: 'currency', currency: item.currencyCode }).format(Number(item.unitPrice)); } catch { return ''; }
    }

    itemMetadata(item) {
      const values = [];
      if (item.size) values.push(this.t('size', { size: item.size }));
      const price = this.money(item);
      if (price) values.push(price);
      if (item.quantityPurchased != null) values.push(this.t('purchased', { count: item.quantityPurchased }));
      return values.length ? `<span class="mymara-returns__meta">${escape(values.join(' · '))}</span>` : '';
    }

    renderItem(item, index) {
      const reference = item.lineItemReference;
      const selected = this.state.selectedItems.get(reference);
      const available = item.eligibility?.eligible === true;
      const id = `${this.id}-item-${index}`;
      const reason = this.t(`eligibility_${item.eligibility?.reasonCode?.toLowerCase()}`) || this.t('eligibility_item_not_eligible');
      const product = `<span class="mymara-returns__product">${this.productText(item, false)}${this.itemMetadata(item)}${available ? '' : `<span class="mymara-returns__eligibility">${escape(reason)}</span>`}</span>`;
      const choice = available
        ? `<label class="mymara-returns__item-choice" for="${id}">${this.image(item)}${product}<span class="mymara-returns__select"><input id="${id}" type="checkbox" data-select-item="${escape(reference)}"${checked(selected)}><span data-selection-label>${escape(this.t(selected ? 'selected' : 'select'))}</span></span></label>`
        : `<div class="mymara-returns__item-choice" aria-disabled="true">${this.image(item)}${product}</div>`;

      return `<div class="mymara-returns__item${selected ? ' is-selected' : ''}${available ? ' is-eligible' : ' is-disabled'}" data-return-item="${escape(reference)}">${choice}${available && item.quantityReturnable > 1 ? `<div class="mymara-returns__quantity"${selected ? '' : ' hidden'}><label for="${id}-quantity">${escape(this.t('quantity'))}</label><input class="field__input" id="${id}-quantity" type="number" inputmode="numeric" min="1" max="${Number(item.quantityReturnable)}" step="1" value="${selected?.quantity || 1}" data-quantity="${escape(reference)}"></div>` : ''}</div>`;
    }

    renderItems() {
      const eligible = this.state.order.items.some((item) => item.eligibility?.eligible === true);
      return `${this.heading('items_title')}${this.summary()}<p>${escape(this.t(eligible ? 'items_intro' : 'items_empty'))}</p><div class="mymara-returns__items">${this.state.order.items.map((item, index) => this.renderItem(item, index)).join('')}</div><div class="mymara-returns__footer">${this.button('restart', 'find_another', !eligible)}${eligible ? this.button('continue', 'continue', true, !this.state.selectedItems.size) : ''}</div>`;
    }

    renderDetails() {
      return `${this.heading('details_title')}<form data-returns-details>${[...this.state.selectedItems].map(([reference, detail], index) => {
        const item = this.item(reference);
        const id = `${this.id}-detail-${index}`;
        const requiredComment = ['OTHER', 'QUALITY_ISSUE'].includes(detail.reason);
        const reasonField = `<div class="mymara-returns__field"><label for="${id}-reason">${escape(this.t('reason'))}</label><select class="field__input" id="${id}-reason" name="${id}-reason" data-detail-field="reason" required><option value="">${escape(this.t('choose_reason'))}</option><optgroup label="${escape(this.t('fit_reasons'))}">${REASONS.slice(0, 4).map((reason) => this.reasonOption(reason, detail.reason)).join('')}</optgroup><optgroup label="${escape(this.t('other_reasons'))}">${REASONS.slice(4).map((reason) => this.reasonOption(reason, detail.reason)).join('')}</optgroup></select></div>`;
        const commentField = `<div class="mymara-returns__field"><label for="${id}-comment" data-comment-label>${escape(this.t(detail.reason === 'QUALITY_ISSUE' ? 'issue_description' : requiredComment ? 'comment_required' : 'comment_optional'))}</label><textarea class="field__input text-area" rows="4" maxlength="2000" id="${id}-comment" name="${id}-comment" data-detail-field="customerComment"${requiredComment ? ' required' : ''}>${escape(detail.customerComment)}</textarea></div>`;
        const actionChoices = `<fieldset class="mymara-returns__actions"><legend>${escape(this.t('action_title'))}</legend>${ACTIONS.map((action) => `<label class="mymara-returns__radio"><input type="radio" name="${id}-action" value="${action}" data-detail-field="requestedAction" required${checked(detail.requestedAction === action)}><span><strong>${escape(this.t(`action_${action.toLowerCase()}`))}</strong><small>${escape(this.t(`action_${action.toLowerCase()}_description`))}</small></span></label>`).join('')}</fieldset>`;
        return `<div class="mymara-returns__detail" data-return-detail="${escape(reference)}"><div class="mymara-returns__compact-product">${this.image(item)}<div class="mymara-returns__product">${this.productText(item)}<span>${escape(this.t('quantity_value', { count: detail.quantity }))}</span></div></div>${reasonField}${commentField}<div data-issue-attachments${detail.reason === 'QUALITY_ISSUE' ? '' : ' hidden'}>${detail.reason === 'QUALITY_ISSUE' ? this.renderPhotos(reference, index) : ''}</div>${actionChoices}<div data-exchange-area${detail.requestedAction === 'EXCHANGE' ? '' : ' hidden'}>${detail.requestedAction === 'EXCHANGE' ? this.renderExchange(reference, index) : ''}</div></div>`;
      }).join('')}<div class="mymara-returns__footer">${this.button('back_items', 'back')}<button class="btn btn-primary" type="submit">${escape(this.t('review_button'))}</button></div></form>`;
    }

    hasMemoryPhotos() {
      return [...this.state.selectedItems.values()].some((detail) => detail.photos?.length || detail.photoRemovals) ||
        this.state.attempt?.body?.items?.some((item) => item.photoReferences?.length);
    }

    photoErrorKey(error) {
      return ({ PHOTO_REQUIRED: 'error_photo_required', PHOTO_TOO_LARGE: 'error_photo_size',
        PHOTO_TYPE_NOT_ALLOWED: 'error_photo_type', PHOTO_LIMIT_EXCEEDED: 'error_photo_limit',
        PHOTO_UPLOAD_EXPIRED: 'error_photo_expired', PHOTO_NOT_FOUND: 'error_photo_expired',
        PHOTO_ALREADY_USED: 'error_photo_expired', PHOTO_NOT_CONFIRMED: 'error_photo_upload',
        RATE_LIMITED: 'error_rate' })[error.code] || 'error_photo_upload';
    }

    renderPhotos(reference, index) {
      const detail = this.state.selectedItems.get(reference);
      const photos = detail.photos || [];
      const inputId = `${this.id}-photos-${index}`;
      return `<div class="mymara-returns__photos"><label for="${inputId}"><strong>${escape(this.t('photos_title'))}</strong></label><p id="${inputId}-help">${escape(this.t('photos_help', { count: this.photoMaxCount, size: this.photoMaxBytes / 1024 / 1024 }))}</p><input id="${inputId}" type="file" accept="image/jpeg,image/png,image/webp" multiple data-photo-input aria-describedby="${inputId}-help"${photos.length >= this.photoMaxCount || detail.photoRemovals ? ' disabled' : ''}><ul class="mymara-returns__photo-list">${photos.map((photo) => `<li class="mymara-returns__photo"><img src="${escape(photo.preview)}" alt="${escape(this.t('photo_preview'))}" width="96" height="96"><div><span class="mymara-returns__photo-name">${escape(photo.file.name)}</span><span role="status">${escape(this.t(photo.status === 'ready' ? 'photo_ready' : photo.status === 'removing' ? 'photo_removing' : photo.status === 'remove_failed' ? 'error_photo_remove' : photo.status === 'failed' ? this.photoErrorKey({ code: photo.error }) : 'photo_uploading'))}</span>${photo.status === 'uploading' ? `<progress aria-label="${escape(this.t('photo_uploading'))}"></progress>` : ''}<div class="mymara-returns__photo-controls">${photo.status === 'failed' ? `<button class="btn btn-outline" type="button" data-photo-retry="${escape(photo.id)}">${escape(this.t('retry'))}</button>` : ''}<button class="btn btn-outline" type="button" data-photo-remove="${escape(photo.id)}">${escape(this.t('photo_remove'))}</button></div></div></li>`).join('')}</ul><p class="mymara-returns__photo-notice">${escape(this.t('photos_private'))}</p></div>`;
    }

    updatePhotos(reference) {
      const area = this.detailElement(reference)?.querySelector('[data-issue-attachments]');
      const detail = this.state.selectedItems.get(reference);
      if (area && detail) {
        area.hidden = detail.reason !== 'QUALITY_ISSUE';
        area.innerHTML = detail.reason === 'QUALITY_ISSUE' ? this.renderPhotos(reference, [...this.state.selectedItems.keys()].indexOf(reference)) : '';
      }
      this.updatePhotoNavigation();
    }

    updatePhotoNavigation() {
      const pending = [...this.state.selectedItems.values()].some((detail) => detail.photoRemovals || detail.photos?.some((photo) => ['uploading', 'removing'].includes(photo.status)));
      const submit = this.view?.querySelector('[type="submit"]');
      if (submit && ['details', 'review'].includes(this.state.step)) submit.disabled = pending || this.state.busy;
    }

    addPhotos(reference, files) {
      const detail = this.state.selectedItems.get(reference);
      if (!detail || detail.reason !== 'QUALITY_ISSUE' || this.state.attempt?.uncertain || detail.photoRemovals) return;
      if (this.state.expiresAt <= Date.now()) { this.handleError({ code: 'SESSION_EXPIRED' }); return; }
      detail.photos ||= [];
      if (detail.photos.length + files.length > this.photoMaxCount) { this.showError('error_photo_limit'); return; }
      if (files.some((file) => !PHOTO_TYPES.includes(file.type))) { this.showError('error_photo_type'); return; }
      if (files.some((file) => !file.size || file.size > this.photoMaxBytes)) { this.showError('error_photo_size'); return; }
      this.hideError();
      for (const file of files) {
        const photo = { id: crypto.randomUUID(), file, preview: URL.createObjectURL(file), status: 'queued' };
        detail.photos.push(photo);
        this.startPhotoUpload(reference, photo);
      }
      this.persistFlowState();
    }

    startPhotoUpload(reference, photo) {
      if (photo.status === 'uploading' || photo.cancelled || this.state.attempt?.uncertain) return;
      photo.status = 'uploading';
      photo.error = null;
      photo.task = this.uploadPendingPhoto(reference, photo);
      this.updatePhotos(reference);
    }

    async uploadPendingPhoto(reference, photo) {
      const detail = this.state.selectedItems.get(reference);
      const sessionToken = this.state.sessionToken;
      if (!detail || detail.reason !== 'QUALITY_ISSUE' || !sessionToken) return;
      const active = () => !photo.cancelled && this.state.sessionToken === sessionToken && this.state.selectedItems.get(reference) === detail && detail.reason === 'QUALITY_ISSUE';
      try {
        if (photo.intent && Date.parse(photo.intent.expiresAt) <= Date.now()) {
          await this.api.removePhoto(sessionToken, photo.intent.photoReference);
          photo.intent = null;
        }
        let confirmed;
        if (photo.intent) {
          try { confirmed = await this.api.confirmPhoto(sessionToken, photo.intent.photoReference); }
          catch (error) {
            if (['PHOTO_NOT_FOUND', 'PHOTO_ALREADY_USED', 'PHOTO_UPLOAD_EXPIRED'].includes(error.code)) {
              if (error.code === 'PHOTO_UPLOAD_EXPIRED') await this.api.removePhoto(sessionToken, photo.intent.photoReference);
              photo.intent = null;
            } else if (error.code !== 'PHOTO_NOT_CONFIRMED') throw error;
          }
        }
        if (!confirmed) {
          if (!photo.intent) photo.intent = await this.api.requestPhotoUpload(sessionToken, reference, photo.file);
          if (!active()) return;
          confirmed = await this.api.uploadPhoto(photo.intent, photo.file, sessionToken);
        }
        if (confirmed?.confirmed !== true || confirmed.photoReference !== photo.intent.photoReference) throw { code: 'PHOTO_UPLOAD_FAILED' };
        if (!active()) return;
        photo.photoReference = confirmed.photoReference;
        photo.status = 'ready';
      } catch (error) {
        if (!active()) return;
        photo.status = 'failed';
        photo.error = error.code || 'PHOTO_UPLOAD_FAILED';
        if (['PHOTO_NOT_FOUND', 'PHOTO_ALREADY_USED', 'PHOTO_UPLOAD_EXPIRED'].includes(error.code)) photo.intent = null;
        if (error.code === 'SESSION_EXPIRED') this.handleError(error);
      } finally {
        if (active()) {
          this.updatePhotos(reference);
          this.persistFlowState();
        }
      }
    }

    async removePendingPhoto(reference, photo) {
      const detail = this.state.selectedItems.get(reference);
      const sessionToken = this.state.sessionToken;
      if (!detail || photo.cancelled) return;
      photo.cancelled = true;
      detail.photoRemovals = (detail.photoRemovals || 0) + 1;
      photo.status = 'removing';
      this.updatePhotos(reference);
      try {
        await photo.task;
        if (photo.intent) {
          try { await this.api.removePhoto(sessionToken, photo.intent.photoReference); }
          catch (error) { if (error.code !== 'PHOTO_NOT_FOUND') throw error; }
        }
        detail.photos = detail.photos.filter((entry) => entry !== photo);
        URL.revokeObjectURL(photo.preview);
      } catch (error) {
        photo.cancelled = false;
        photo.status = 'remove_failed';
        if (this.state.sessionToken === sessionToken) {
          if (error.code === 'SESSION_EXPIRED') this.handleError(error);
          else this.showError('error_photo_remove');
        }
      } finally {
        detail.photoRemovals--;
        if (this.state.selectedItems.get(reference) === detail) this.updatePhotos(reference);
        this.persistFlowState();
      }
    }

    disposePhotos(detail, sessionToken, deleteRemote = true) {
      if (!detail) return;
      const photos = detail.photos || [];
      detail.photos = [];
      for (const photo of photos) {
        photo.cancelled = true;
        if (!deleteRemote) { URL.revokeObjectURL(photo.preview); continue; }
        detail.photoRemovals = (detail.photoRemovals || 0) + 1;
        Promise.resolve(photo.task).then(async () => {
          if (photo.intent) {
            try { await this.api.removePhoto(sessionToken, photo.intent.photoReference); }
            catch (error) { if (error.code !== 'PHOTO_NOT_FOUND') throw error; }
          }
          URL.revokeObjectURL(photo.preview);
        }).catch(() => {
          if (this.state.sessionToken === sessionToken && [...this.state.selectedItems.values()].includes(detail)) {
            photo.cancelled = false;
            photo.status = 'remove_failed';
            detail.photos.push(photo);
          } else URL.revokeObjectURL(photo.preview);
        }).finally(() => {
          detail.photoRemovals--;
          const entry = [...this.state.selectedItems].find(([, value]) => value === detail);
          if (entry) this.updatePhotos(entry[0]);
          this.persistFlowState();
        });
      }
    }

    validatePhotos() {
      for (const [reference, detail] of this.state.selectedItems) {
        if (detail.reason !== 'QUALITY_ISSUE') continue;
        const photos = detail.photos || [];
        const key = detail.photoRemovals || photos.some((photo) => photo.status === 'uploading') ? 'error_photo_pending'
          : photos.length > this.photoMaxCount ? 'error_photo_limit'
          : !photos.length ? 'error_photo_required'
          : photos.some((photo) => photo.status !== 'ready' || !photo.photoReference) ? 'error_photo_upload'
          : photos.some((photo) => Date.parse(photo.intent?.expiresAt) <= Date.now()) ? 'error_photo_expired' : null;
        if (key) {
          if (this.state.step === 'review') this.go('details');
          this.showError(key, null, this.detailElement(reference)?.querySelector('[data-photo-input]'));
          return false;
        }
      }
      return true;
    }

    reasonOption(reason, current) {
      return `<option value="${reason}"${reason === current ? ' selected' : ''}>${escape(this.t(`reason_${reason.toLowerCase()}`))}</option>`;
    }

    detailElement(reference) {
      return [...this.querySelectorAll('[data-return-detail]')].find((element) => element.dataset.returnDetail === reference);
    }

    renderExchange(reference, index) {
      const detail = this.state.selectedItems.get(reference);
      const item = this.item(reference);
      if (!detail.options) return `<p role="status">${escape(this.t(detail.optionsError ? 'error_unavailable' : 'loading_sizes'))}</p>${detail.optionsError ? `<button type="button" class="btn btn-outline" data-retry-exchange="${escape(reference)}">${escape(this.t('retry'))}</button>` : this.spinner}`;
      if (!detail.options.some((option) => option.available === true)) return `<p>${escape(this.t('exchange_empty'))}</p>`;
      return `<fieldset class="mymara-returns__sizes"><legend>${escape(this.t('exchange_title'))}</legend>${item.size ? `<p>${escape(this.t('current_size', { size: item.size }))}</p>` : ''}<div class="mymara-returns__swatches">${detail.options.map((option, optionIndex) => {
        const id = `${this.id}-size-${index}-${optionIndex}`;
        return `<div><input id="${id}" class="visually-hidden" type="radio" name="${this.id}-exchange-${index}" value="${escape(option.reference)}" data-detail-field="requestedExchangeVariantReference"${option.available === true ? ' required' : ' disabled'}${checked(detail.requestedExchangeVariantReference === option.reference)}><label for="${id}">${escape(option.size || option.title)}<span class="visually-hidden"> ${escape(this.t(option.available === true ? 'available' : 'unavailable'))}</span></label></div>`;
      }).join('')}</div><p data-new-size>${escape(detail.requestedExchangeVariantReference ? this.t('new_size', { size: detail.options.find((option) => option.reference === detail.requestedExchangeVariantReference)?.size || detail.options.find((option) => option.reference === detail.requestedExchangeVariantReference)?.title }) : '')}</p></fieldset>`;
    }

    async loadExchange(reference) {
      const detail = this.state.selectedItems.get(reference);
      if (!detail || detail.optionsLoading) return;
      const sessionToken = this.state.sessionToken;
      detail.optionsLoading = true;
      detail.optionsError = false;
      this.updateExchange(reference);
      try {
        const data = await this.api.getExchangeOptions(sessionToken, reference);
        if (!Array.isArray(data.options)) throw new window.MyMaraReturnsApiError('SERVER_ERROR', 502);
        if (this.state.sessionToken !== sessionToken || this.state.selectedItems.get(reference) !== detail) return;
        detail.options = data.options;
      } catch (error) {
        if (this.state.sessionToken !== sessionToken || this.state.selectedItems.get(reference) !== detail) return;
        detail.optionsError = true;
        if (['SESSION_EXPIRED', 'RETURN_NOT_ELIGIBLE', 'RETURN_CONFLICT', 'RATE_LIMITED'].includes(error.code) || error.status === 429) this.handleError(error, 'exchange');
      } finally {
        detail.optionsLoading = false;
        if (this.state.sessionToken === sessionToken && this.state.step === 'details') this.updateExchange(reference);
      }
    }

    updateExchange(reference) {
      const area = this.detailElement(reference)?.querySelector('[data-exchange-area]');
      if (!area) return;
      const detail = this.state.selectedItems.get(reference);
      area.hidden = detail.requestedAction !== 'EXCHANGE';
      area.innerHTML = detail.requestedAction === 'EXCHANGE' ? this.renderExchange(reference, [...this.state.selectedItems.keys()].indexOf(reference)) : '';
    }

    onInput(event) {
      const control = event.target;
      if (control.dataset.detailField === 'customerComment') {
        const detail = this.state.selectedItems.get(control.closest('[data-return-detail]').dataset.returnDetail);
        detail.customerComment = control.value;
        control.setCustomValidity('');
        this.persistFlowState();
      }
    }

    onChange(event) {
      if (this.state.busy) return;
      const control = event.target;
      if (control.matches('[data-photo-input]')) {
        const reference = control.closest('[data-return-detail]').dataset.returnDetail;
        this.addPhotos(reference, [...control.files]);
        control.value = '';
        return;
      }
      if (control.dataset.selectItem) {
        const reference = control.dataset.selectItem;
        const item = this.item(reference);
        if (item?.eligibility?.eligible !== true) return;
        if (control.checked) this.state.selectedItems.set(reference, { quantity: 1, requestedAction: '', reason: '', customerComment: '', requestedExchangeVariantReference: '', options: null, photos: [] });
        else {
          this.disposePhotos(this.state.selectedItems.get(reference), this.state.sessionToken);
          this.state.selectedItems.delete(reference);
        }
        const card = control.closest('[data-return-item]');
        card.classList.toggle('is-selected', control.checked);
        card.querySelector('[data-selection-label]').textContent = this.t(control.checked ? 'selected' : 'select');
        const quantityArea = card.querySelector('.mymara-returns__quantity');
        if (quantityArea) {
          quantityArea.hidden = !control.checked;
          quantityArea.querySelector('input').value = '1';
        }
        this.querySelector('[data-returns-action="continue"]').disabled = !this.state.selectedItems.size;
      } else if (control.dataset.quantity) {
        const detail = this.state.selectedItems.get(control.dataset.quantity);
        if (detail && control.checkValidity()) detail.quantity = Number(control.value);
      } else if (control.dataset.detailField) {
        const reference = control.closest('[data-return-detail]').dataset.returnDetail;
        const detail = this.state.selectedItems.get(reference);
        detail[control.dataset.detailField] = control.value;
        if (control.dataset.detailField === 'requestedAction') {
          if (control.value !== 'EXCHANGE') detail.requestedExchangeVariantReference = '';
          this.updateExchange(reference);
          if (control.value === 'EXCHANGE' && !detail.options) this.loadExchange(reference);
        } else if (control.dataset.detailField === 'reason') {
          if (control.value !== 'QUALITY_ISSUE') this.disposePhotos(detail, this.state.sessionToken);
          this.updatePhotos(reference);
          const group = this.detailElement(reference);
          const required = ['OTHER', 'QUALITY_ISSUE'].includes(control.value);
          const comment = group.querySelector('textarea');
          comment.required = required;
          comment.setCustomValidity('');
          group.querySelector('[data-comment-label]').textContent = this.t(control.value === 'QUALITY_ISSUE' ? 'issue_description' : required ? 'comment_required' : 'comment_optional');
        } else if (control.dataset.detailField === 'requestedExchangeVariantReference') {
          const option = detail.options.find((entry) => entry.reference === control.value);
          this.detailElement(reference).querySelector('[data-new-size]').textContent = this.t('new_size', { size: option.size || option.title });
        }
      } else if (control.matches('[data-acknowledgement]')) this.state.acknowledged = control.checked;
      this.persistFlowState();
    }

    onClick(event) {
      const photoButton = event.target.closest('[data-photo-remove], [data-photo-retry]');
      if (photoButton) {
        if (this.state.busy || this.state.attempt?.uncertain) return;
        const reference = photoButton.closest('[data-return-detail]').dataset.returnDetail;
        const photo = this.state.selectedItems.get(reference)?.photos?.find((entry) => entry.id === (photoButton.dataset.photoRemove || photoButton.dataset.photoRetry));
        if (photo) {
          if (photoButton.dataset.photoRemove) this.removePendingPhoto(reference, photo);
          else this.startPhotoUpload(reference, photo);
        }
        return;
      }
      const retry = event.target.closest('[data-retry-exchange]');
      if (retry) { this.loadExchange(retry.dataset.retryExchange); return; }
      const button = event.target.closest('[data-returns-action]');
      if (!button || this.state.busy) return;
      const action = button.dataset.returnsAction;
      if (['restart', 'refresh'].includes(action)) {
        if (this.state.attempt?.uncertain) return;
        this.clearSession();
        this.go('lookup');
        if (action === 'refresh') this.announcement.textContent = this.t('refresh_explanation');
      } else if (action === 'continue') {
        if (!this.state.selectedItems.size) return;
        const inputs = [...this.querySelectorAll('[data-quantity]')].filter((input) => !input.closest('[hidden]'));
        if (inputs.some((input) => !input.reportValidity())) return;
        inputs.forEach((input) => { this.state.selectedItems.get(input.dataset.quantity).quantity = Number(input.value); });
        this.go('details');
      } else if (action === 'back_items') this.go('items');
      else if (action === 'edit_details') this.go('details');
      else if (action === 'retry_status') this.loadStatus();
    }

    validateDetails(form) {
      this.hideError();
      for (const [reference, detail] of this.state.selectedItems) {
        const group = this.detailElement(reference);
        const textarea = group.querySelector('textarea');
        textarea.setCustomValidity(textarea.required && detail.customerComment.trim().length < 5 ? this.t('error_comment') : '');
        if (detail.requestedAction === 'EXCHANGE' && !detail.options?.some((option) => option.available === true && option.reference === detail.requestedExchangeVariantReference)) {
          this.showError('error_choose_size', null, group.querySelector('[data-detail-field="requestedAction"]:checked'));
          return false;
        }
      }
      return this.validatePhotos() && form.reportValidity();
    }

    reviewItem(item, detail, edit = false) {
      const replacement = detail.options?.find((option) => option.reference === detail.requestedExchangeVariantReference);
      return `<article class="mymara-returns__review-item"><div class="mymara-returns__compact-product">${this.image(item)}<div class="mymara-returns__product">${this.productText(item)}<span>${escape(this.t('quantity_value', { count: detail.quantity }))}</span></div></div><dl><div><dt>${escape(this.t('action_title'))}</dt><dd>${escape(this.t(`action_${detail.requestedAction.toLowerCase()}`))}${replacement ? `<br>${escape(this.t('size_change', { from: item.size || item.variantTitle || '', to: replacement.size || replacement.title }))}` : ''}</dd></div><div><dt>${escape(this.t('reason'))}</dt><dd>${escape(this.t(`reason_${detail.reason.toLowerCase()}`))}</dd></div>${detail.customerComment.trim() ? `<div><dt>${escape(this.t('additional_details'))}</dt><dd class="mymara-returns__preline">${escape(detail.customerComment)}</dd></div>` : ''}</dl>${detail.reason === 'QUALITY_ISSUE' ? `<p>${escape(this.t('photos_attached', { count: detail.submittedPhotoCount ?? detail.photos?.filter((photo) => photo.status === 'ready').length ?? 0 }))}</p>` : ''}${edit ? this.button('edit_details', 'edit', false, this.state.attempt?.uncertain) : ''}</article>`;
    }

    renderReview() {
      const locked = this.state.attempt?.uncertain;
      return `${this.heading('review_title')}<form>${[...this.state.selectedItems].map(([reference, detail]) => this.reviewItem(this.item(reference), detail, true)).join('')}<div class="mymara-returns__policy"><h2 class="h5">${escape(this.t('policy_title'))}</h2><ul><li>${escape(this.t('policy_shipping'))}</li><li>${escape(this.t('policy_condition'))}</li><li>${escape(this.t('policy_processing'))}</li><li>${escape(this.t('policy_eligibility'))}</li></ul>${this.dataset.policyUrl ? `<a class="link" href="${escape(this.dataset.policyUrl)}" target="_blank" rel="noopener noreferrer">${escape(this.t('policy_link'))}</a>` : ''}<label class="mymara-returns__acknowledgement"><input type="checkbox" data-acknowledgement required${checked(this.state.acknowledged)}${locked ? ' disabled' : ''}><span>${escape(this.t('policy_acknowledgement'))}</span></label></div><div class="mymara-returns__footer">${this.button('edit_details', 'back', false, locked)}<button type="submit" class="btn btn-primary">${escape(this.t(locked ? 'retry_submission' : 'submit'))}</button></div></form>`;
    }

    submissionBody() {
      return {
        sessionToken: this.state.sessionToken,
        locale: this.lang || undefined,
        items: [...this.state.selectedItems].map(([lineItemReference, detail]) => ({
          lineItemReference, quantity: detail.quantity,
          requestedAction: detail.requestedAction, reason: detail.reason,
          ...(detail.customerComment.trim() ? { customerComment: detail.customerComment.trim() } : {}),
          ...(detail.reason === 'QUALITY_ISSUE' ? { photoReferences: (detail.photos || []).filter((photo) => photo.status === 'ready').map((photo) => photo.photoReference) } : {}),
          ...(detail.requestedAction === 'EXCHANGE' ? { requestedExchangeVariantReference: detail.requestedExchangeVariantReference } : {})
        }))
      };
    }

    async submitReturn() {
      if (this.state.busy) return;
      this.hideError();
      if (!this.state.attempt?.uncertain && !this.validatePhotos()) return;
      if (this.photoSubmissionInterrupted) return;
      const body = this.submissionBody();
      if (!this.state.attempt || (!this.state.attempt.uncertain && JSON.stringify(this.state.attempt.body) !== JSON.stringify(body))) {
        this.state.attempt = { key: crypto.randomUUID(), body, uncertain: false };
      }
      const attempt = this.state.attempt;
      this.setBusy(true, 'submitting');
      try {
        const data = await this.api.createReturn(attempt.body, attempt.key);
        if (!data.returnNumber || !data.statusToken) throw new window.MyMaraReturnsApiError('SERVER_ERROR', 502);
        this.confirmationItems = [...this.state.selectedItems].map(([reference, detail]) => ({ item: this.item(reference), detail: { ...detail, submittedPhotoCount: detail.photos?.filter((photo) => photo.status === 'ready').length || 0 } }));
        this.confirmation = data;
        this.setBusy(false);
        this.clearSession(true, false);
        this.state.step = 'confirmation';
        this.syncStepInUrl(null);
        this.render();
        this.loadConfirmationInstructions(data.statusToken);
      } catch (error) {
        this.setBusy(false);
        attempt.uncertain = !error.status || error.status >= 500 || (attempt.uncertain && error.status === 429);
        this.render(false);
        this.handleError(error, 'create');
        this.persistFlowState();
      }
    }

    statusUrl(token) {
      const url = new URL(this.dataset.statusUrl, window.location.origin);
      url.searchParams.set('token', token);
      return `${url.pathname}${url.search}`;
    }

    renderConfirmation() {
      return `${this.heading('confirmation_title')}<p>${escape(this.t('reference_label'))}</p><p class="h2 mymara-returns__reference">${escape(this.confirmation.returnNumber)}</p><p>${escape(this.t('include_reference', { reference: this.confirmation.returnNumber }))}</p><div class="mymara-returns__next"><h2 class="h4">${escape(this.t('next_title'))}</h2><ol><li>${escape(this.t('next_pack'))}</li><li>${escape(this.t('next_reference'))}</li><li>${escape(this.t('next_instructions'))}</li><li>${escape(this.t('next_process'))}</li></ol><div data-confirmation-instructions></div></div><h2 class="h4">${escape(this.t('submitted_items'))}</h2>${this.confirmationItems.map(({ item, detail }) => this.reviewItem(item, detail)).join('')}<a class="btn btn-primary" rel="noreferrer" href="${escape(this.statusUrl(this.confirmation.statusToken))}">${escape(this.t('view_status'))}</a>`;
    }

    async loadConfirmationInstructions(token) {
      try {
        const data = await this.api.getReturnStatus(token);
        if (this.state.step === 'confirmation') this.querySelector('[data-confirmation-instructions]').innerHTML = this.instructions(data.instructions);
      } catch {}
    }

    instructions(instructions) {
      if (!instructions) return '';
      return `<div class="mymara-returns__instructions"><h2 class="h4">${escape(this.t('instructions_title'))}</h2>${instructions.body ? `<p class="mymara-returns__preline">${escape(instructions.body)}</p>` : ''}${instructions.address ? `<h3 class="h5">${escape(this.t('return_address'))}</h3><p class="mymara-returns__preline">${escape(instructions.address)}</p>` : ''}${instructions.shippingArrangedBy === 'CUSTOMER' ? `<p>${escape(this.t('policy_shipping'))}</p>` : ''}${instructions.includeReturnNumber ? `<p>${escape(this.t('next_reference'))}</p>` : ''}</div>`;
    }

    readStatusToken() {
      const url = new URL(window.location.href);
      const supplied = window.MymaraReturnsStatusToken !== undefined || url.searchParams.has('token');
      let token = window.MymaraReturnsStatusToken ?? url.searchParams.get('token');
      delete window.MymaraReturnsStatusToken;
      if (url.searchParams.has('token')) {
        url.searchParams.delete('token');
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
      try {
        if (supplied) sessionStorage.setItem('mymara:returns:status', JSON.stringify({ token, expiresAt: Date.now() + 1800000 }));
        else {
          const cached = JSON.parse(sessionStorage.getItem('mymara:returns:status'));
          if (cached?.expiresAt > Date.now()) token = cached.token;
          else sessionStorage.removeItem('mymara:returns:status');
        }
      } catch {}
      return token;
    }

    async loadStatus() {
      if (this.state.busy) return;
      this.hideError();
      this.progress.hidden = true;
      this.statusToken = this.statusToken || this.readStatusToken();
      if (!this.statusToken) { this.invalidStatus(); return; }
      this.view.innerHTML = `${this.heading('status_title')}<p>${escape(this.t('loading_status'))}</p>${this.spinner}`;
      this.setBusy(true, 'loading_status');
      try {
        const data = await this.api.getReturnStatus(this.statusToken);
        if (!data.returnNumber || !Array.isArray(data.items) || !Array.isArray(data.timeline)) throw new window.MyMaraReturnsApiError('SERVER_ERROR', 502);
        this.setBusy(false);
        this.view.innerHTML = this.renderStatus(data);
        this.view.querySelector('[data-returns-heading]').focus();
      } catch (error) {
        this.setBusy(false);
        if (['RETURN_NOT_FOUND', 'SESSION_EXPIRED'].includes(error.code) || error.status === 404 || error.status === 401) this.invalidStatus();
        else this.handleError(error, 'status');
      }
    }

    invalidStatus() {
      this.statusToken = null;
      try { sessionStorage.removeItem('mymara:returns:status'); } catch {}
      this.view.innerHTML = `${this.heading('status_not_found')}<a class="btn btn-primary" href="${escape(this.dataset.returnsUrl)}">${escape(this.t('start_new'))}</a>`;
      this.view.querySelector('[data-returns-heading]').focus();
    }

    date(value) {
      const date = new Date(value);
      return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(this.lang || document.documentElement.lang, { day: 'numeric', month: 'long', year: 'numeric' }).format(date) : '';
    }

    statusLabel(status) {
      return STATUSES.includes(status) ? this.t(`status_${status.toLowerCase()}`) : this.t('status_updated');
    }

    renderStatus(data) {
      return `<h1 class="h2" tabindex="-1" data-returns-heading>${escape(this.t('status_return', { reference: data.returnNumber }))}</h1><p>${escape(this.t('submitted_on', { date: this.date(data.createdAt) }))}</p><div class="mymara-returns__current-status"><h2 class="h5">${escape(this.t('current_status'))}</h2><p class="h3">${escape(this.statusLabel(data.status))}</p>${this.t(`status_instruction_${data.status.toLowerCase()}`) ? `<p>${escape(this.t(`status_instruction_${data.status.toLowerCase()}`))}</p>` : ''}</div><ol class="mymara-returns__timeline" aria-label="${escape(this.t('timeline'))}">${data.timeline.map((entry, index) => `<li${index === data.timeline.length - 1 && entry.status === data.status ? ' aria-current="step"' : ''}><span aria-hidden="true">${index === data.timeline.length - 1 && entry.status === data.status ? '●' : '✓'}</span><div><strong>${escape(this.statusLabel(entry.status))}</strong><span>${escape(this.date(entry.createdAt))}</span></div></li>`).join('')}</ol>${this.instructions(data.instructions)}<h2 class="h4">${escape(this.t('submitted_items'))}</h2>${data.items.map((item) => `<article class="mymara-returns__status-item"><div class="mymara-returns__product">${this.productText(item)}<span>${escape(this.t('quantity_value', { count: item.quantity }))}</span><strong>${escape(this.t(`action_${String(item.requestedAction).toLowerCase()}`))}</strong>${item.requestedExchangeSize ? `<span>${escape(this.t('size_change', { from: item.size || '', to: item.requestedExchangeSize }))}</span>` : ''}</div></article>`).join('')}<a class="link" href="${escape(this.dataset.returnsUrl)}">${escape(this.t('start_new'))}</a>`;
    }
  }

  if (!customElements.get('mymara-returns')) customElements.define('mymara-returns', MyMaraReturns);
})();
